import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateMatchBody, MatchSummary } from '@war-game/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { api, errorMessage } from '../api/client';
import { keys, useMatches } from '../api/queries';
import { TopBar } from '../components/TopBar';
import { Alert, Badge, Button, Empty, Field, Loading, Modal, Panel } from '../components/ui';
import { dtg } from '../lib/format';
import { InfoTip } from '../components/help/InfoTip';
import { Tour } from '../components/help/Tour';
import type { HelpId } from '../help/topics';
import { useAuth } from '../store/auth';

const VICTORY: Array<[CreateMatchBody['victory']['mode'], string]> = [
  ['supremacy', 'Supremacia (maior Pontuação de Poder após N turnos)'],
  ['economic', 'Maior potência econômica (PIB)'],
  ['military', 'Maior potência militar'],
  ['capitals', 'Conquista de capitais (> 50% por 3 turnos)'],
  ['control', 'Controle (> 70% dos territórios por 3 turnos)'],
  ['domination', 'Dominação total'],
  ['nuclear-terror', 'Terror nuclear'],
];

/** Modos com prazo: a partida termina no turno limite (os outros terminam por conquista). */
const TIMED_MODES: ReadonlyArray<CreateMatchBody['victory']['mode']> = ['supremacy', 'economic', 'military'];

/** Turno de 5 minutos e tempo real 24× (playtest). */
const FAST_TURN_MINUTES = 5;

export function CreateMatchDialog({
  open,
  onOpenChange,
  onCreated,
  singleOnly = false,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (id: string) => void;
  /** Standalone: só single player (sem o seletor de modo). */
  singleOnly?: boolean;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateMatchBody>({
    name: '',
    maxPlayers: 8,
    turnsPerDay: 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
    victory: { mode: 'supremacy', turnLimit: 90, sharedAlliance: false },
    luck: true,
    maxAllies: 2,
    // Completar com bots é decidido no lobby, ao iniciar.
    bots: { fill: false, difficulty: 'medium' },
    inactiveToBot: false,
    realtimeSpeed: 1,
    private: false,
    singlePlayer: singleOnly,
  });
  const [fast, setFast] = useState(false);
  const single = form.singlePlayer;
  const timed = TIMED_MODES.includes(form.victory.mode);
  const m = useMutation({
    mutationFn: () =>
      api.call('createMatch', {
        body: fast && !single ? { ...form, turnMinutes: FAST_TURN_MINUTES, realtimeSpeed: 24 } : form,
      }),
    onSuccess: (match) => {
      void qc.invalidateQueries({ queryKey: keys.matches });
      onOpenChange(false);
      onCreated(match.id);
    },
  });
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={singleOnly ? 'Nova campanha' : 'Nova partida'} wide>
      <form
        className="grid gap-3 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        {!singleOnly && (
          <div
            role="radiogroup"
            aria-label="Modo da partida"
            className="grid grid-cols-2 gap-0.5 font-mono text-[11px] md:col-span-2"
          >
            {(
              [
                [false, 'Multijogador', 'Turnos no relógio; outros jogadores entram pelo lobby.'],
                [
                  true,
                  'Single player',
                  'Você contra bots; o turno vira quando você clicar em Próximo turno.',
                ],
              ] as const
            ).map(([value, label, hint]) => (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={single === value}
                onClick={() => setForm({ ...form, singlePlayer: value })}
                className={`flex flex-col items-start gap-0.5 px-3 py-2 text-left ${single === value ? 'bg-amber text-ink-900' : 'border border-line-2 text-fg-2 hover:border-amber'}`}
              >
                <span className="font-bold tracking-[0.1em] uppercase">{label}</span>
                <span className={single === value ? 'text-ink-900/80' : 'text-dim'}>{hint}</span>
              </button>
            ))}
          </div>
        )}
        <Field label="Nome">
          <input
            className="c2-input"
            required
            minLength={3}
            maxLength={60}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label={single ? 'Governos (você + bots, 4 a 12)' : 'Governos (4 a 12)'} info="match.players">
          <input
            className="c2-input"
            type="number"
            min={4}
            max={12}
            value={form.maxPlayers}
            onChange={(e) => setForm({ ...form, maxPlayers: Number(e.target.value) })}
          />
        </Field>
        {!singleOnly && (
          <>
            <Field
              label="Turnos por dia"
              info="match.turns"
              hint={single ? 'Single player: o turno vira no botão Próximo turno.' : undefined}
            >
              <select
                className="c2-input disabled:opacity-40"
                value={form.turnsPerDay}
                disabled={fast || single}
                onChange={(e) => setForm({ ...form, turnsPerDay: Number(e.target.value) as 1 | 2 })}
              >
                <option value={1}>1 (meia-noite)</option>
                <option value={2}>2 (meia-noite e meio-dia)</option>
              </select>
            </Field>
            <Field label="Fuso horário" info="match.timezone">
              <input
                className="c2-input disabled:opacity-40"
                disabled={single}
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              />
            </Field>
          </>
        )}
        <Field label="Modo de vitória" info="match.victory">
          <select
            className="c2-input"
            value={form.victory.mode}
            onChange={(e) =>
              setForm({
                ...form,
                victory: { ...form.victory, mode: e.target.value as CreateMatchBody['victory']['mode'] },
              })
            }
          >
            {VICTORY.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Turno limite (N)"
          info="match.turnLimit"
          hint={timed ? undefined : 'Este modo termina por conquista, sem turno limite.'}
        >
          <input
            className="c2-input disabled:opacity-40"
            type="number"
            min={10}
            max={365}
            disabled={!timed}
            value={form.victory.turnLimit}
            onChange={(e) =>
              setForm({ ...form, victory: { ...form.victory, turnLimit: Number(e.target.value) } })
            }
          />
        </Field>
        <Field label="Limite de aliados" info="match.allies">
          <input
            className="c2-input"
            type="number"
            min={0}
            max={11}
            value={form.maxAllies}
            onChange={(e) => setForm({ ...form, maxAllies: Number(e.target.value) })}
          />
        </Field>
        <Field label="Dificuldade dos bots" info="match.bots">
          <select
            className="c2-input"
            value={form.bots.difficulty}
            onChange={(e) =>
              setForm({ ...form, bots: { ...form.bots, difficulty: e.target.value as 'easy' } })
            }
          >
            <option value="easy">Fácil</option>
            <option value="medium">Médio</option>
            <option value="hard">Difícil</option>
          </select>
        </Field>
        <div className="flex flex-col gap-2 font-mono text-[11px] md:col-span-2">
          {(
            [
              [
                'Partida fechada (só entra quem tiver o código de convite)',
                form.private,
                (v: boolean) => setForm({ ...form, private: v }),
                'match.private',
                !single,
              ],
              [
                'Sorte no combate (±10%)',
                form.luck,
                (v: boolean) => setForm({ ...form, luck: v }),
                'match.luck',
                true,
              ],
              [
                'Vitória compartilhada por aliança',
                form.victory.sharedAlliance,
                (v: boolean) => setForm({ ...form, victory: { ...form.victory, sharedAlliance: v } }),
                'match.sharedVictory',
                true,
              ],
              [
                'Governo inativo passa para um bot (em vez de colapsar)',
                form.inactiveToBot,
                (v: boolean) => setForm({ ...form, inactiveToBot: v }),
                'match.inactive',
                !single,
              ],
              [
                `Partida rápida de playtest (turnos de ${FAST_TURN_MINUTES} min, tempo real 24×)`,
                fast,
                setFast,
                'match.fast',
                // Single player é por turno: sem relógio, não há o que acelerar.
                !single,
              ],
            ] as Array<[string, boolean, (v: boolean) => void, HelpId, boolean]>
          )
            .filter(([, , , , visible]) => visible)
            .map(([label, value, set, help]) => (
              <div key={label} className="flex items-center gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => set(e.target.checked)}
                    className="accent-amber"
                  />
                  {label}
                </label>
                <InfoTip topic={help} />
              </div>
            ))}
        </div>
        {m.error && (
          <div className="md:col-span-2">
            <Alert>{errorMessage(m.error)}</Alert>
          </div>
        )}
        <div className="flex justify-end gap-2 md:col-span-2">
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button variant="primary" type="submit" disabled={m.isPending}>
            {singleOnly ? 'Criar campanha' : 'Criar partida'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Entrar numa partida (aberta ou fechada) pelo código de convite. */
function JoinByCode() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const find = useMutation({
    mutationFn: (c: string) => api.call('matchByCode', { params: { code: c } }),
    onSuccess: (match) => navigate(`/partidas/${match.id}/lobby?convite=${encodeURIComponent(code.trim())}`),
  });
  return (
    <form
      className="flex flex-wrap items-center gap-2 font-mono text-[11px]"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim()) find.mutate(code.trim().toUpperCase());
      }}
    >
      <span className="text-dim">CÓDIGO DE CONVITE</span>
      <input
        className="c2-input h-8 w-28 uppercase"
        maxLength={12}
        value={code}
        aria-label="Código de convite"
        placeholder="ABC123"
        onChange={(e) => setCode(e.target.value)}
      />
      <Button type="submit" disabled={!code.trim() || find.isPending}>
        Abrir lobby
      </Button>
      {find.error && <span className="text-hostile-2">{errorMessage(find.error)}</span>}
    </form>
  );
}

function MatchRow({ m }: { m: MatchSummary }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const running = m.status === 'RUNNING' || m.status === 'PAUSED' || m.status === 'FINISHED';
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-2 border-b border-line px-3 py-3 font-mono text-[11px] md:grid-cols-[2fr_1fr_1fr_1fr_auto]">
      <div>
        <div className="text-[12px] font-bold text-fg">
          {m.name}{' '}
          {m.singlePlayer ? (
            <Badge tone="friend">Single player</Badge>
          ) : (
            m.private && <Badge tone="dim">Fechada</Badge>
          )}
        </div>
        <div className="text-dim">{m.players.map((p) => p.government).join(' · ')}</div>
      </div>
      <div className="hidden md:block">
        <Badge tone={m.status === 'RUNNING' ? 'neutral' : m.status === 'LOBBY' ? 'amber' : 'dim'}>
          {t(`lobby.status.${m.status}`)}
        </Badge>
      </div>
      <div className="hidden text-muted md:block">
        {m.players.length}/{m.maxPlayers} ·{' '}
        {m.singlePlayer
          ? 'turno manual'
          : m.turnMinutes < 720
            ? `${m.turnMinutes} min/turno`
            : `${m.turnsPerDay}×/dia`}
      </div>
      <div className="hidden text-muted md:block">
        {running
          ? `T${m.currentTurn}${m.nextTurnAt ? ` · ${dtg(new Date(m.nextTurnAt).getTime())}` : ''}`
          : '—'}
      </div>
      <div className="flex gap-2">
        {running && m.joined ? (
          <Button variant="primary" onClick={() => navigate(`/partidas/${m.id}/mapa`)}>
            Abrir
          </Button>
        ) : m.status === 'LOBBY' ? (
          <Button onClick={() => navigate(`/partidas/${m.id}/lobby`)}>{m.joined ? 'Lobby' : 'Ver'}</Button>
        ) : null}
      </div>
    </div>
  );
}

export function LobbyPage() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const { data, isLoading } = useMatches();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  // Links antigos (?partida=ID) abrem o lobby da partida.
  const legacy = new URLSearchParams(window.location.search).get('partida');
  return (
    <div className="flex min-h-full flex-col bg-ink-900">
      <TopBar />
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-4 p-4">
        {user && !user.phoneVerified && (
          <Alert tone="amber">
            {t('auth.verifyPending')}{' '}
            <button type="button" className="underline" onClick={() => navigate('/confirmar-celular')}>
              {t('auth.verifyAction')}
            </button>
          </Alert>
        )}
        {legacy && <Navigate to={`/partidas/${legacy}/lobby`} replace />}
        <Panel
          title={t('lobby.title')}
          tour="lobby-list"
          right={
            <span data-tour="lobby-new">
              <Button variant="primary" onClick={() => setCreating(true)} disabled={!user?.phoneVerified}>
                {t('lobby.create')}
              </Button>
            </span>
          }
        >
          {isLoading ? (
            <Loading />
          ) : data?.length ? (
            data.map((m) => <MatchRow key={m.id} m={m} />)
          ) : (
            <Empty>{t('lobby.empty')}</Empty>
          )}
          <div className="border-t border-line px-3 py-2.5">
            <JoinByCode />
          </div>
        </Panel>
      </main>
      <Tour id="lobby" />
      <CreateMatchDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => navigate(`/partidas/${id}/lobby`)}
      />
    </div>
  );
}
